import {
  GetNotifications200Response,
  NotificationTypeResponse,
} from "../../codegen-api";
import { BoundedKeySet } from "../../utils/boundedKeySet";
import {
  getNotificationActivitySeconds,
  getNotificationDedupeKey,
} from "../../utils/notifications";

// Only pop a toast for genuinely-recent events. The notifications list is fed by
// both the REST baseline and the WSS snapshot, and the two can disagree (e.g. the
// snapshot includes yesterday's deposit that the REST baseline omitted). Without
// this guard, that day-old deposit looks "new" the moment the snapshot lands and
// toasts repeatedly. We use the real event time (metadata.timestamp), so a
// late-delivered or re-snapshotted old event is never surfaced as a toast.
export const RECENT_TOAST_WINDOW_SEC = 120;

/** Above the 100 rows the list keeps, so a key still on screen is never forgotten. */
export const SEEN_NOTIFICATION_KEYS_LIMIT = 500;
export const SEEN_NOTIFICATION_ACCOUNTS_LIMIT = 20;

export interface NotificationSeenState {
  byAccount: Map<string, BoundedKeySet>;
}

export const createNotificationSeenState = (): NotificationSeenState => ({
  byAccount: new Map(),
});

const ownedBy = (notification: GetNotifications200Response, accountKey: string) => {
  const owner =
    typeof notification.account === "string" ? notification.account.toLowerCase() : "";
  return !owner || owner === accountKey;
};

/**
 * The notifications to announce now for `account` (FE-7, FE-20).
 *
 * Seen keys are remembered PER ACCOUNT and bounded. The first list seen for an
 * account only primes its keys (nothing toasts on load); switching wallets
 * primes the new account instead of announcing its whole history, and
 * switching back does not re-announce. Rows owned by another account are
 * ignored, and only events inside RECENT_TOAST_WINDOW_SEC are announced.
 */
export const collectNewNotifications = (
  state: NotificationSeenState,
  account: string | null | undefined,
  notifications: GetNotifications200Response[] | undefined,
  nowSec: number
): GetNotifications200Response[] => {
  if (!account || !notifications?.length) return [];
  const accountKey = account.toLowerCase();
  const own = notifications.filter((n) => ownedBy(n, accountKey));
  if (!own.length) return [];

  const seen = state.byAccount.get(accountKey);
  if (!seen) {
    const primed = new BoundedKeySet(SEEN_NOTIFICATION_KEYS_LIMIT);
    own.forEach((n) => primed.add(getNotificationDedupeKey(n)));
    state.byAccount.set(accountKey, primed);
    while (state.byAccount.size > SEEN_NOTIFICATION_ACCOUNTS_LIMIT) {
      state.byAccount.delete(state.byAccount.keys().next().value as string);
    }
    return [];
  }

  const fresh: GetNotifications200Response[] = [];
  own.forEach((n) => {
    if (!seen.add(getNotificationDedupeKey(n))) return;
    // Suppress toasts for stale events (re-snapshotted / late-delivered). When
    // no event time is available we keep the previous behaviour and surface it.
    const activitySec = getNotificationActivitySeconds(n);
    if (activitySec !== null && nowSec - activitySec > RECENT_TOAST_WINDOW_SEC) {
      return;
    }
    fresh.push(n);
  });
  return fresh;
};

export const isLiquidationNotification = (notification: GetNotifications200Response) =>
  notification.notification_type === NotificationTypeResponse.LiquidationStarted ||
  notification.notification_type === NotificationTypeResponse.LiquidationCompleted;

export const getNotificationTxHash = (
  notification: GetNotifications200Response
): string | undefined => {
  const metadata = (notification.metadata || {}) as Record<string, unknown>;
  const hash = metadata.tx_hash ?? metadata.txHash;
  return typeof hash === "string" && hash ? hash : undefined;
};
